import { api, getActiveEntityId } from "@/lib/api"
import type { BookId } from "@/lib/types"
import { bookToApi } from "@/routes/books/mapping"

/**
 * Los tres estados CONTABLES. Modulo `ledger/statements` del backend.
 *
 *   GET /api/ledger/statements/balance-sheet ?entity_id&libro&hasta[&inicio_ejercicio]
 *   GET /api/ledger/statements/income        ?entity_id&libro&desde&hasta
 *   GET /api/ledger/statements/cash-flow     ?entity_id&libro&desde&hasta
 *
 * NO son los de `finance/`. Aquellos salen del modelo financiero, admiten
 * criterio y se editan; estos salen de los ASIENTOS y tienen que cuadrar. La
 * pantalla `/finanzas/hoja-vs-libro` existe para comparar las dos lecturas.
 *
 * Este archivo NO mapea a camelCase y es a proposito. El backend devuelve un
 * arbol de secciones y lineas cuya forma ES la del estado contable, y traducirlo
 * a otro vocabulario obligaria a mantener dos nombres para cada subtotal. Los
 * tipos de abajo describen lo que llega, tal cual llega.
 */

function entidadActiva(): string {
  const entityId = getActiveEntityId()
  if (!entityId) {
    throw new Error(
      "Elige una sociedad en la barra superior. Los estados contables salen de " +
        "los libros de una sociedad, no de la empresa: un balance que suma " +
        "cuatro paises no es el balance de nadie.",
    )
  }
  return entityId
}

/** Una cuenta dentro de una seccion, ya con el signo de presentacion. */
export interface StatementLine {
  codigo: string
  nombre: string
  importe: number
  /** `true` cuando la linea NO sale de un saldo del libro (resultado del ejercicio). */
  derivado?: boolean
}

/** Lo que el estado no pudo clasificar. Ver `no_mapeado` en el backend. */
export interface NoMapeado {
  hay: boolean
  neto: number
  /**
   * El BRUTO importa: un asiento entero sin mapear netea a cero, el balance
   * cuadra, y sin embargo faltan importes en todas las lineas del estado.
   */
  bruto: number
}

export interface BalanceSheet {
  libro: string
  corte: string
  inicio_ejercicio: string
  activo: { total: number; lineas: StatementLine[] }
  pasivo: { total: number; lineas: StatementLine[] }
  patrimonio: { total: number; lineas: StatementLine[] }
  resultado_del_ejercicio: number
  sin_mapear: number
  no_mapeado: NoMapeado
  asientos: number
  libro_vacio: boolean
  comprobacion: {
    activo: number
    pasivo_mas_patrimonio: number
    sin_mapear: number
    diferencia: number
    cuadra: boolean
    nota: string
    aviso_no_mapeado: string | null
  }
}

export interface IncomeStatement {
  libro: string
  desde: string
  hasta: string
  secciones: Array<{
    codigo: string
    clave: string
    etiqueta: string
    importe: number
    cuentas: StatementLine[]
  }>
  subtotales: {
    utilidad_bruta: number
    ebitda: number
    utilidad_operativa: number
    antes_de_impuestos: number
    utilidad_neta: number
  }
  sin_mapear: number
  no_mapeado: NoMapeado
  asientos: number
  libro_vacio: boolean
  comprobacion: {
    utilidad_neta_por_cascada: number
    utilidad_neta_por_suma_de_saldos: number
    cuadra: boolean
  }
}

export type CashFlowActivity = "operacion" | "inversion" | "financiamiento" | "sin_clasificar"

export interface CashFlowStatement {
  libro: string
  desde: string
  hasta: string
  metodo: string
  cuentas_de_efectivo: string[]
  actividades: Record<CashFlowActivity, { total: number; lineas: StatementLine[] }>
  variacion: number
  efectivo_inicial: number
  efectivo_final: number
  asientos: number
  libro_vacio: boolean
  comprobacion: {
    efectivo_inicial_mas_variacion: number
    efectivo_final: number
    diferencia: number
    cuadra: boolean
    nota: string
  }
}

export function fetchBalanceSheet(params: {
  book: BookId
  cutoff: string
}): Promise<BalanceSheet> {
  return api.get<BalanceSheet>("/ledger/statements/balance-sheet", {
    query: {
      entity_id: entidadActiva(),
      libro: bookToApi(params.book),
      hasta: params.cutoff,
    },
  })
}

export function fetchIncomeStatement(params: {
  book: BookId
  from: string
  to: string
}): Promise<IncomeStatement> {
  return api.get<IncomeStatement>("/ledger/statements/income", {
    query: {
      entity_id: entidadActiva(),
      libro: bookToApi(params.book),
      desde: params.from,
      hasta: params.to,
    },
  })
}

export function fetchCashFlow(params: {
  book: BookId
  from: string
  to: string
}): Promise<CashFlowStatement> {
  return api.get<CashFlowStatement>("/ledger/statements/cash-flow", {
    query: {
      entity_id: entidadActiva(),
      libro: bookToApi(params.book),
      desde: params.from,
      hasta: params.to,
    },
  })
}
